import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import { guestsApi, roomTypesApi, reservationsApi } from '../services/api';
import { format } from 'date-fns';

interface ReservationFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ReservationFormDialog: React.FC<ReservationFormDialogProps> = ({ open, onClose, onSuccess }) => {
  const [guests, setGuests] = useState<any[]>([]);
  const [roomTypes, setRoomTypes] = useState<any[]>([]);
  const [availableRooms, setAvailableRooms] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    guest_id: '',
    room_type_id: '',
    room_id: '',
    check_in_date: new Date(),
    check_out_date: new Date(Date.now() + 24 * 60 * 60 * 1000),
    adults: 1,
    children: 0,
    special_requests: '',
  });

  useEffect(() => {
    if (open) {
      fetchGuests();
      fetchRoomTypes();
    }
  }, [open]);

  useEffect(() => {
    if (formData.check_in_date && formData.check_out_date && formData.room_type_id) {
      checkRoomAvailability();
    }
  }, [formData.check_in_date, formData.check_out_date, formData.room_type_id]);

  const fetchGuests = async () => {
    try {
      const response = await guestsApi.getAll();
      setGuests(response.data.data || []);
    } catch (err) {
      console.error('Failed to load guests:', err);
    }
  };

  const fetchRoomTypes = async () => {
    try {
      const response = await roomTypesApi.getAll();
      setRoomTypes(response.data.data || []);
    } catch (err) {
      console.error('Failed to load room types:', err);
    }
  };

  const checkRoomAvailability = async () => {
    if (!formData.check_in_date || !formData.check_out_date) return;

    try {
      setCheckingAvailability(true);
      const params: any = {
        check_in: format(formData.check_in_date, 'yyyy-MM-dd'),
        check_out: format(formData.check_out_date, 'yyyy-MM-dd'),
      };

      if (formData.room_type_id) {
        params.room_type_id = formData.room_type_id;
      }

      const response = await reservationsApi.checkAvailability(params);
      setAvailableRooms(response.data.data || []);
    } catch (err) {
      console.error('Failed to check availability:', err);
      setAvailableRooms([]);
    } finally {
      setCheckingAvailability(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.guest_id) {
      setError('Please select a guest');
      return;
    }

    if (!formData.check_in_date || !formData.check_out_date) {
      setError('Please select check-in and check-out dates');
      return;
    }

    if (formData.check_in_date >= formData.check_out_date) {
      setError('Check-out date must be after check-in date');
      return;
    }

    try {
      setLoading(true);
      const reservationData = {
        guest_id: parseInt(formData.guest_id),
        room_id: formData.room_id ? parseInt(formData.room_id) : null,
        check_in_date: format(formData.check_in_date, 'yyyy-MM-dd'),
        check_out_date: format(formData.check_out_date, 'yyyy-MM-dd'),
        adults: parseInt(formData.adults.toString()),
        children: parseInt(formData.children.toString()),
        special_requests: formData.special_requests || null,
      };

      await reservationsApi.create(reservationData);
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create reservation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>New Reservation</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Guest"
                value={formData.guest_id}
                onChange={(e) => setFormData({ ...formData, guest_id: e.target.value })}
                required
              >
                <MenuItem value="">Select Guest</MenuItem>
                {guests.map((guest) => (
                  <MenuItem key={guest.id} value={guest.id}>
                    {guest.first_name} {guest.last_name} {guest.email ? `(${guest.email})` : ''}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Check-in Date"
                value={formData.check_in_date ? format(formData.check_in_date, 'yyyy-MM-dd') : ''}
                onChange={(e) => {
                  const date = e.target.value ? new Date(e.target.value) : new Date();
                  setFormData({ ...formData, check_in_date: date });
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: format(new Date(), 'yyyy-MM-dd') }}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Check-out Date"
                value={formData.check_out_date ? format(formData.check_out_date, 'yyyy-MM-dd') : ''}
                onChange={(e) => {
                  const date = e.target.value ? new Date(e.target.value) : new Date();
                  setFormData({ ...formData, check_out_date: date });
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ 
                  min: formData.check_in_date 
                    ? format(new Date(formData.check_in_date.getTime() + 24 * 60 * 60 * 1000), 'yyyy-MM-dd')
                    : format(new Date(), 'yyyy-MM-dd')
                }}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Room Type (Optional)"
                value={formData.room_type_id}
                onChange={(e) => setFormData({ ...formData, room_type_id: e.target.value, room_id: '' })}
              >
                <MenuItem value="">Any Room Type</MenuItem>
                {roomTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name} - ${type.base_rate}/night
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {checkingAvailability && (
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Checking room availability...</Typography>
                </Box>
              </Grid>
            )}

            {availableRooms.length > 0 && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  select
                  label="Select Room"
                  value={formData.room_id}
                  onChange={(e) => setFormData({ ...formData, room_id: e.target.value })}
                >
                  <MenuItem value="">No room assigned (will assign later)</MenuItem>
                  {availableRooms.map((room) => (
                    <MenuItem key={room.id} value={room.id}>
                      Room {room.room_number} - {room.room_type.name} (${room.room_type.base_rate}/night)
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Adults"
                value={formData.adults}
                onChange={(e) => setFormData({ ...formData, adults: parseInt(e.target.value) || 1 })}
                inputProps={{ min: 1 }}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Children"
                value={formData.children}
                onChange={(e) => setFormData({ ...formData, children: parseInt(e.target.value) || 0 })}
                inputProps={{ min: 0 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Special Requests"
                value={formData.special_requests}
                onChange={(e) => setFormData({ ...formData, special_requests: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Create Reservation'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ReservationFormDialog;

